/**
 * Global Search Bar Component
 * Provides search across all entities in the project
 */
import React, { useState, useEffect, useRef } from 'react';
import { useProjectStore, usePoleStore } from '../../stores';
import { useViewStore } from '../../store/viewStore';
import { searchAll, SearchResult, searchCustomers, NajdenyZakaznik } from '../../services/searchService';
import { farbaVlakna, kdeVisi, prejdiNaZakaznika } from '../../utils/customerSearch';
import './SearchBar.css';

interface SearchBarProps {
  onResultClick?: (result: SearchResult) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onResultClick }) => {
  const { currentProject } = useProjectStore();
  const { setViewMode } = useViewStore();
  const { poles, setSelectedPole, setZoomTarget } = usePoleStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  // Zákazníci sa hľadajú vo VŠETKÝCH projektoch, na ktoré má používateľ právo —
  // technik pri poruche nevie, v ktorom projekte daný človek je.
  const [zakaznici, setZakaznici] = useState<NajdenyZakaznik[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle Ctrl+F / Cmd+F keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setIsExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }

      // Close on Escape
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsExpanded(false);
        setQuery('');
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsExpanded(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      setZakaznici([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      // Dve nezávislé hľadania: prvé v otvorenom projekte (mapa, topológia,
      // štatistiky), druhé po zákazníkoch naprieč projektami. Keď jedno zlyhá,
      // druhé sa aj tak zobrazí.
      const [vProjekte, zakaznikov] = await Promise.allSettled([
        currentProject ? searchAll(query, currentProject.id) : Promise.resolve(null),
        query.trim().length >= 2 ? searchCustomers(query, 10) : Promise.resolve(null),
      ]);

      if (vProjekte.status === 'fulfilled') {
        setResults(vProjekte.value);
      } else {
        console.error('Search failed:', vProjekte.reason);
        setResults(null);
      }

      if (zakaznikov.status === 'fulfilled') {
        setZakaznici(zakaznikov.value?.results || []);
      } else {
        console.error('Hľadanie zákazníka zlyhalo:', zakaznikov.reason);
        setZakaznici([]);
      }

      setSelectedIndex(0);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, currentProject]);

  // Get all results as flat array for keyboard navigation
  const getAllResults = () => {
    if (!results) return [];
    return [
      ...results.results.map,
      ...results.results.topology,
      ...results.results.statistics,
      ...results.results.documentation
    ];
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const allResults = getAllResults();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      e.preventDefault();
      handleResultClick(allResults[selectedIndex]);
    }
  };

  // Handle result click
  const handleResultClick = (result: any) => {
    // Switch to appropriate view
    if (result.category === 'map') {
      setViewMode('map');

      // If it's a pole with coordinates, zoom to it
      if (result.type !== 'cable' && result.coordinates?.lat && result.coordinates?.lng) {
        // Find the pole in store
        const pole = poles.find(p => p.id === result.id);
        if (pole) {
          setSelectedPole(pole);
        }

        // Set zoom target
        setZoomTarget({
          lat: result.coordinates.lat,
          lng: result.coordinates.lng,
          zoom: 18
        });
      }
    } else if (result.category === 'topology') {
      // TODO: Open topology dialog and highlight result
    } else if (result.category === 'statistics') {
      // TODO: Open statistics dialog
    }

    setIsOpen(false);
    setQuery('');

    if (onResultClick) {
      onResultClick(result);
    }
  };

  const totalResults = (results ? results.total : 0) + zakaznici.length;

  // Klik na zákazníka: prepne projekt (ak treba), skočí na stĺpik a otvorí kazetu
  const otvorZakaznika = async (z: NajdenyZakaznik) => {
    setIsOpen(false);
    setIsExpanded(false);
    setQuery('');
    await prejdiNaZakaznika(z, { otvorKazetu: true });
  };

  const zavriHladanie = () => {
    setIsExpanded(false);
    setIsOpen(false);
    setQuery('');
    setResults(null);
    setZakaznici([]);
  };

  return (
    <div className="search-bar-container collapsed" ref={searchRef}>
      <button
        className="search-icon-button"
        onClick={() => {
          setIsExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        title="Hľadať (Ctrl+F)"
      >
        🔍
      </button>

      {/* Hľadanie ide na celú obrazovku. V hlavičke bolo políčko také úzke, že
          sa mená zákazníkov orezávali na „Andr…" — a prekrytie zároveň odloží
          menu do pozadia, takže technikovi nič neprekáža. */}
      {isExpanded && (
        <div
          className="search-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) zavriHladanie();
          }}
        >
          <div className="search-overlay-panel">
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                ref={inputRef}
                type="search"
                className="search-input"
                placeholder="Meno, adresa, telefón, stĺpik…"
                autoComplete="off"
                enterKeyHint="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
              />
              {loading && <span className="search-loading">⏳</span>}
              {query && (
                <button
                  className="search-clear"
                  onClick={() => {
                    setQuery('');
                    setResults(null);
                    setZakaznici([]);
                    inputRef.current?.focus();
                  }}
                  title="Vymazať"
                >
                  ✕
                </button>
              )}
              <button className="search-close" onClick={zavriHladanie} title="Zavrieť">
                Zavrieť
              </button>
            </div>

            <div className="search-overlay-body">
              {!query && (
                <div className="search-hint">
                  <p>
                    <b>Zákazníka</b> hľadám vo všetkých projektoch, na ktoré máš právo —
                    podľa mena, adresy, telefónu, čísla zákazníka, názvu stĺpika alebo
                    sériového čísla ONU.
                  </p>
                  {currentProject && (
                    <p>
                      <b>Stĺpy, káble a topológiu</b> hľadám v otvorenom projekte{' '}
                      {currentProject.name}.
                    </p>
                  )}
                </div>
              )}

              {query && loading && !results && zakaznici.length === 0 && (
                <div className="search-hint">Hľadám…</div>
              )}

              {query && (results || zakaznici.length > 0) && (
        <div className="search-results-dropdown">
          {totalResults === 0 ? (
            <div className="search-no-results">
              <span className="no-results-icon">🔍</span>
              <p>Žiadne výsledky pre "{query}"</p>
            </div>
          ) : (
            <>
              <div className="search-results-header">
                <span>Nájdených: {totalResults}</span>
              </div>

              {/* Zákazníci — naprieč projektami, preto sú prví */}
              {zakaznici.length > 0 && (
                <div className="search-category">
                  <div className="search-category-header">
                    <span className="category-icon">👤</span>
                    <span className="category-title">ZÁKAZNÍCI ({zakaznici.length})</span>
                  </div>
                  {zakaznici.map((z, i) => {
                    const farba = farbaVlakna(z.vystup);
                    return (
                      <div
                        key={`zak-${z.projekt.id}-${z.stlpik.id}-${z.vystup ?? 'x'}-${i}`}
                        className="search-result-item"
                        onClick={() => otvorZakaznika(z)}
                      >
                        <span className="result-icon">{z.zdroj === 'onu' ? '📶' : '👤'}</span>
                        <div className="result-content">
                          <div className="result-name">
                            {z.meno}
                            {farba && (
                              <span
                                className="search-fiber-chip"
                                style={{ background: farba.hex, color: farba.text }}
                              >
                                #{z.vystup} {farba.nazov}
                              </span>
                            )}
                          </div>
                          <div className="result-description">
                            {z.projekt.nazov} · {kdeVisi(z)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Map Results */}
              {results && results.results.map.length > 0 && (
                <div className="search-category">
                  <div className="search-category-header">
                    <span className="category-icon">📍</span>
                    <span className="category-title">MAPA ({results.results.map.length})</span>
                  </div>
                  {results.results.map.map((result, index) => (
                    <div
                      key={`map-${result.id}`}
                      className={`search-result-item ${selectedIndex === index ? 'selected' : ''}`}
                      onClick={() => handleResultClick(result)}
                    >
                      <span className="result-icon">{result.icon}</span>
                      <div className="result-content">
                        <div className="result-name">{result.name}</div>
                        <div className="result-description">{result.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Topology Results */}
              {results && results.results.topology.length > 0 && (
                <div className="search-category">
                  <div className="search-category-header">
                    <span className="category-icon">🔀</span>
                    <span className="category-title">TOPOLÓGIA ({results.results.topology.length})</span>
                  </div>
                  {results.results.topology.map((result, index) => {
                    const globalIndex = results.results.map.length + index;
                    return (
                      <div
                        key={`topology-${result.id}`}
                        className={`search-result-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                        onClick={() => handleResultClick(result)}
                      >
                        <span className="result-icon">{result.icon}</span>
                        <div className="result-content">
                          <div className="result-name">{result.name}</div>
                          <div className="result-description">{result.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Statistics Results */}
              {results && results.results.statistics.length > 0 && (
                <div className="search-category">
                  <div className="search-category-header">
                    <span className="category-icon">📊</span>
                    <span className="category-title">ŠTATISTIKY ({results.results.statistics.length})</span>
                  </div>
                  {results.results.statistics.map((result, index) => {
                    const globalIndex = results.results.map.length + results.results.topology.length + index;
                    return (
                      <div
                        key={`statistics-${result.id}`}
                        className={`search-result-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                        onClick={() => handleResultClick(result)}
                      >
                        <span className="result-icon">{result.icon}</span>
                        <div className="result-content">
                          <div className="result-name">{result.name}</div>
                          <div className="result-description">{result.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

